import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import type { AppDispatch } from './index';
import type { ShiftDistribution } from '../types/shiftDistributions';

export type { ShiftDistribution };

interface ShiftDistributionsState {
  items: ShiftDistribution[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ShiftDistributionsState = {
  items: [],
  isLoading: false,
  error: null,
};

const shiftDistributionsSlice = createSlice({
  name: 'shiftDistributions',
  initialState,
  reducers: {
    setItems(state, action: PayloadAction<ShiftDistribution[]>) {
      state.items = action.payload;
    },
    upsertItem(state, action: PayloadAction<ShiftDistribution>) {
      const idx = state.items.findIndex((d) => d.date === action.payload.date);
      if (idx !== -1) {
        state.items[idx] = action.payload;
      } else {
        state.items.push(action.payload);
      }
    },
    removeItem(state, action: PayloadAction<string>) {
      state.items = state.items.filter((d) => d.date !== action.payload);
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const { setItems, upsertItem, removeItem, setLoading, setError } =
  shiftDistributionsSlice.actions;

export const fetchShiftDistributions =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await apiService.listShiftDistributions();
      dispatch(setItems(response.data ?? []));
    } catch (error: any) {
      dispatch(setError(error?.message ?? 'Failed to load shift distributions'));
    } finally {
      dispatch(setLoading(false));
    }
  };

export default shiftDistributionsSlice.reducer;
